import type { ResearchConfigInput } from '@shared/config';
import type { RecipeInput } from '@shared/recipes';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, FlaskConical, Search } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { Field, Input } from '../../components/ui/form';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/session';

interface FieldCandidate {
  key: string;
  label: string;
  selector: string;
  coverage: number;
  samples: string[];
}

interface AnalysisResponse {
  url: string;
  cardSelector?: string;
  cardCount: number;
  fields: FieldCandidate[];
  recipe?: RecipeInput;
  usedJsonLd: boolean;
  notes: string[];
}

interface TestResponse {
  matched: number;
  rejected: number;
  nextUrl: string | null;
  preview: Array<{ externalId: string; detailUrl: string | null; values: Record<string, unknown[]> }>;
  notes: string[];
}

export interface SetupAnyWebsiteProps {
  config: ResearchConfigInput;
  onChange: (patch: Partial<ResearchConfigInput>) => void;
  error?: string | undefined;
}

/**
 * Point at a listing page, see what the analyser detected, test it, then confirm.
 *
 * The analyser only ever proposes structure — nothing is treated as extracted data until the
 * preview has been run against the live page and accepted here.
 */
export function SetupAnyWebsite({ config, onChange, error }: SetupAnyWebsiteProps) {
  const options = config.sourceOptions?.any_website;
  const [url, setUrl] = useState(options?.startUrls?.[0] ?? '');
  const [analysis, setAnalysis] = useState<AnalysisResponse | null>(null);
  const [test, setTest] = useState<TestResponse | null>(null);

  const analyze = useMutation({
    mutationFn: (target: string) => api.post<AnalysisResponse>('/api/sites/analyze', { url: target }),
    onSuccess: (result) => {
      setAnalysis(result);
      setTest(null);
    },
  });

  const runTest = useMutation({
    mutationFn: (recipe: RecipeInput) => api.post<TestResponse>('/api/sites/test', { url, recipe }),
    onSuccess: setTest,
  });

  const confirm = (): void => {
    if (!analysis?.recipe) return;
    onChange({
      sourceOptions: {
        ...config.sourceOptions,
        any_website: { startUrls: [url], recipe: analysis.recipe, maxPages: options?.maxPages ?? 10 },
      },
    });
  };

  const confirmed = Boolean(options?.recipe);

  return (
    <div className="space-y-4">
      {error ? <Alert tone="error">{error}</Alert> : null}

      <Field label="Listing page URL" hint="A page that lists businesses — search results, a directory category, a member list.">
        {({ id, describedBy }) => (
          <span className="flex gap-2">
            <Input
              id={id}
              type="url"
              value={url}
              placeholder="https://example.com/directory/restaurants"
              aria-describedby={describedBy}
              onChange={(event) => setUrl(event.target.value)}
            />
            <Button
              variant="outline"
              icon={<Search className="h-3.5 w-3.5" />}
              loading={analyze.isPending}
              disabled={!url.trim()}
              onClick={() => analyze.mutate(url.trim())}
            >
              Analyse
            </Button>
          </span>
        )}
      </Field>

      {analyze.error ? <Alert tone="error">{errorMessage(analyze.error)}</Alert> : null}
      {analyze.isPending ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Fetching and analysing the page…
        </div>
      ) : null}

      {analysis ? (
        <div className="space-y-3 rounded-[var(--radius-panel)] border border-border p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-medium text-foreground">
              {analysis.cardCount > 0 ? `Detected ${analysis.cardCount} repeated listings` : 'No repeated listings detected'}
            </span>
            {analysis.cardSelector ? <Badge tone="neutral">{analysis.cardSelector}</Badge> : null}
            {analysis.usedJsonLd ? <Badge tone="success">schema.org data found</Badge> : null}
          </div>

          {analysis.fields.length > 0 ? (
            <ul className="space-y-1">
              {analysis.fields.map((field) => (
                <li key={field.key} className="grid grid-cols-[120px_minmax(0,1fr)_64px] items-start gap-2 text-[13px]">
                  <span className="font-medium text-foreground">{field.label}</span>
                  <span className="min-w-0 truncate text-muted-foreground" title={field.samples.join(' | ')}>
                    {field.samples[0] ?? '—'}
                  </span>
                  <span className="tabular text-right text-muted-foreground">{Math.round(field.coverage * 100)}%</span>
                </li>
              ))}
            </ul>
          ) : null}

          {analysis.notes.map((note) => (
            <p key={note} className="text-[13px] text-muted-foreground">
              {note}
            </p>
          ))}

          {analysis.recipe ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                icon={<FlaskConical className="h-3.5 w-3.5" />}
                loading={runTest.isPending}
                onClick={() => analysis.recipe && runTest.mutate(analysis.recipe)}
              >
                Test on this page
              </Button>
              <Button size="sm" disabled={!test || test.matched === 0} onClick={confirm}>
                Use this extraction
              </Button>
              {confirmed ? (
                <span className="flex items-center gap-1 text-[13px] text-success">
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Confirmed
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {runTest.error ? <Alert tone="error">{errorMessage(runTest.error)}</Alert> : null}

      {test ? (
        <div className="space-y-2 rounded-[var(--radius-panel)] border border-border p-3">
          <p className="text-[13px] font-medium text-foreground">
            Test run: {test.matched} listing(s) extracted
            {test.rejected > 0 ? `, ${test.rejected} skipped` : ''}
          </p>
          {test.preview.map((row) => (
            <div key={row.externalId} className="border-b border-border pb-1.5 text-[13px] last:border-b-0">
              {Object.entries(row.values).map(([key, values]) => (
                <div key={key} className="grid grid-cols-[110px_minmax(0,1fr)] gap-2">
                  <span className="text-muted-foreground">{key}</span>
                  <span className="min-w-0 truncate text-foreground">{values.map(String).join(', ')}</span>
                </div>
              ))}
            </div>
          ))}
          {test.notes.map((note) => (
            <p key={note} className="text-[13px] text-warning">
              {note}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  );
}
